import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";

const displayValue = (value) => (value ? value : "-");

const TaxOfficeDetailsDialog = ({ open, onClose, taxOffice, taxOfficeId }) => {
  const [details, setDetails] = useState(taxOffice || null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (taxOffice) {
      setDetails(taxOffice);
      return;
    }

    const id = taxOfficeId;
    if (!id) {
      setDetails(null);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    axios
      .get(`/api/v1/taxOffices/${id}`)
      .then((response) => {
        if (!isCancelled) {
          setDetails(response.data);
        }
      })
      .catch((error) => {
        console.error("Error loading tax office details:", error);
        if (!isCancelled) {
          setDetails(null);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [open, taxOffice, taxOfficeId]);

  const countryLabel = [details?.countryCode, details?.countryName].filter(Boolean).join(" - ");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="view-tax-office-details-title"
    >
      <DialogTitle id="view-tax-office-details-title">View Tax Office</DialogTitle>
      <DialogContent dividers sx={{ px: 3, py: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Name"
                value={displayValue(details?.name)}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "Tax office name" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Country"
                value={displayValue(countryLabel)}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "Country" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street"
                value={displayValue(details?.street)}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "Street" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Building Number"
                value={displayValue(details?.buildingIdentifier)}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "Building number" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Postal Code"
                value={displayValue(details?.postalCode)}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "Postal code" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={displayValue(details?.city)}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "City" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tax Office Number"
                value={displayValue(details?.taxOfficeNumber)}
                InputProps={{ readOnly: true }}
                inputProps={{ "aria-label": "Tax office number" }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button type="button" onClick={onClose} aria-label="Close tax office details">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaxOfficeDetailsDialog;
